/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { Spin } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { groupGamesByDay } from '@sportninja/common/features/game/gameListView/gameDayBucket'
import type { GameLike } from '@sportninja/common/features/game/gameListView/types'

import { font, weightRegular, weightSemiBold } from 'src/components/css'
import type { CalendarAgendaViewProps } from './types'
import CalendarGameCard from './CalendarGameCard'
import { formatCivilDay, todayKey } from './buildCalendarDays'
import { calendarLabel } from './calendarI18n'

const CalendarAgendaView = ({
  items,
  isSoccer,
  isHockey,
  loading,
}: CalendarAgendaViewProps) => {
  'use memo'

  if (loading) {
    return (
      <div css={loadingStyles}>
        <Spin size='small' />
      </div>
    )
  }

  //  Venue-local grouping via the shared bucketer, so the agenda cannot drift
  //  from the month grid, the day grid or the list on the same dataset.
  const groups = groupGamesByDay(items as unknown as GameLike[])
  const today = todayKey()

  if (groups.length === 0) {
    return <div css={emptyStyles}>{calendarLabel('calendarNoGamesPeriod')}</div>
  }

  return (
    <div css={containerStyles}>
      {groups.map((group) => {
        const isToday = group.dayKey === today
        const ordered = [
          ...(group.tbdGames as unknown as CalendarAgendaViewProps['items']),
          ...(group.games as unknown as CalendarAgendaViewProps['items']),
        ]

        return (
          <div key={group.dayKey} css={dayGroupStyles}>
            <div css={dayHeaderStyles(isToday)}>
              <div>
                <div css={dateTextStyles(isToday)}>
                  {formatCivilDay(group.dayKey, 'cccc, LLLL d, yyyy')}
                </div>
                <div css={gameCountStyles}>
                  {calendarLabel('calendarGamesScheduled', {
                    count: ordered.length,
                  })}
                </div>
              </div>
              {isToday && (
                <span css={todayBadgeStyles}>
                  {calendarLabel('calendarToday')}
                </span>
              )}
            </div>
            <div css={gamesContainerStyles}>
              {ordered.map((game) => (
                <CalendarGameCard
                  key={game.id}
                  game={game}
                  isSoccer={isSoccer}
                  isHockey={isHockey}
                />
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ─── Styles ──────────────────────────────────────────────────────

const containerStyles = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const dayGroupStyles = css`
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 8px;
  overflow: hidden;
`

const dayHeaderStyles = (isToday: boolean) => css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: ${colors.SECONDARY_400};
  ${isToday ? `box-shadow: inset 3px 0 0 ${colors.PRIMARY};` : ''}
  border-bottom: 1px solid ${colors.SECONDARY_200};
`

const dateTextStyles = (isToday: boolean) => css`
  ${font.body}
  ${weightSemiBold}
  font-size: 14px;
  color: ${isToday ? colors.PRIMARY : colors.NEUTRAL_0};
  line-height: 21px;
`

const gameCountStyles = css`
  ${font.body}
  ${weightRegular}
  font-size: 13px;
  color: ${colors.NEUTRAL_70};
  line-height: 16px;
`

const todayBadgeStyles = css`
  ${font.body}
  ${weightSemiBold}
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: ${colors.PRIMARY_TEXT};
  background: ${colors.PRIMARY};
  padding: 4px 12px;
  border-radius: 4px;
  flex-shrink: 0;
`

const gamesContainerStyles = css`
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: ${colors.SECONDARY_500};
`

const loadingStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 80px 0;
`

const emptyStyles = css`
  ${font.body}
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  font-size: 14px;
  color: ${colors.NEUTRAL_50};
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 8px;
  background: ${colors.SECONDARY_500};
`

export default CalendarAgendaView
